import { onIdTokenChanged, type Auth, type User } from "firebase/auth";
import { doc, onSnapshot, type Firestore } from "firebase/firestore";
import { subscribeRevisions, type RevisionEvent, type RevisionSource, type RealtimeStatus } from "./index";

/** Explicit recovery also works on a continuously focused operational screen. */
export const retryRealtimeConnection = () => {
  if (typeof window !== "undefined") window.dispatchEvent(new Event("a2z:realtime-retry"));
};
const recoveryEvents = ["focus", "online", "a2z:realtime-retry"] as const;
const delays = [1000, 5000, 15_000];

/** One credential exchange, bounded retries after failure, then explicit recovery. */
export function startFirebaseCredential({ signIn, isAuthenticated, isCurrent, onError }: {
  signIn: () => Promise<unknown>;
  isAuthenticated: () => boolean;
  isCurrent: () => boolean;
  onError?: () => void;
}): () => void {
  let active = true;
  let inFlight = false;
  let failed = true;
  let attempts = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let watchdog: ReturnType<typeof setTimeout> | undefined;
  const current = () => active && isCurrent();
  const run = async () => {
    if (!current() || inFlight || (!failed && isAuthenticated())) return;
    clearTimeout(timer);
    inFlight = true;
    // A stalled SDK exchange is visible, but remains single-flight until it settles.
    watchdog = setTimeout(() => { if (current()) onError?.(); }, 10_000);
    try { await signIn(); if (current()) { failed = false; attempts = 0; } }
    catch {
      if (current()) {
        failed = true; onError?.();
        if (attempts < delays.length) timer = setTimeout(() => void run(), delays[attempts++]);
      }
    } finally { clearTimeout(watchdog); inFlight = false; }
  };
  const wake = (event: Event) => {
    // Focus/online in a background tab are ignored; an explicit retry (e.g. a
    // paired print PC's periodic recovery) must work while the tab is hidden.
    if (event.type !== "a2z:realtime-retry" && typeof document !== "undefined" && document.visibilityState === "hidden") return;
    if (inFlight) return;
    attempts = 0;
    void run();
  };
  if (typeof window !== "undefined") recoveryEvents.forEach(event => window.addEventListener(event, wake));
  void run();
  return () => {
    active = false; clearTimeout(timer); clearTimeout(watchdog);
    if (typeof window !== "undefined") recoveryEvents.forEach(event => window.removeEventListener(event, wake));
  };
}

/** Reuse the portal's Firebase auth owner; this adapter never signs anyone in. */
export function subscribeAuthenticatedRevisions({
  auth, db, ownerId, ownerType, storeId, sources, isCurrent, onChange, onError, onStatus,
}: {
  auth: Auth;
  db: Firestore;
  ownerId: string;
  ownerType: "SELLER" | "ADMIN" | "BUYER";
  storeId?: string;
  sources: readonly RevisionSource[];
  isCurrent: () => boolean;
  onChange: (event: RevisionEvent) => void;
  onError?: () => void;
  onStatus?: (status: RealtimeStatus) => void;
}): () => void {
  let active = true;
  let generation = 0;
  let hadIdentity = false;
  let stop: (() => void) | undefined;
  let attempts = 0;
  let retry: ReturnType<typeof setTimeout> | undefined;
  let watchdog: ReturnType<typeof setTimeout> | undefined;
  const current = () => active && isCurrent();
  // The no-live watchdog covers an OPEN listener only. Boot, the custom-token
  // exchange and sign-in are not listener time, so they stay "connecting"; a
  // failed exchange reports itself through startFirebaseCredential's onError.
  const watch = () => {
    clearTimeout(watchdog);
    watchdog = setTimeout(() => { if (current()) onStatus?.("degraded"); }, 10_000);
  };
  const close = () => { stop?.(); stop = undefined; clearTimeout(watchdog); };
  const failed = () => {
    close();
    if (!current()) return;
    onStatus?.("degraded"); onError?.();
    clearTimeout(retry);
    if (attempts < delays.length) retry = setTimeout(() => authenticate(auth.currentUser), delays[attempts++]);
  };
  const authenticate = (user: User | null) => {
    const ticket = ++generation;
    if (!current() || user?.uid !== ownerId) {
      close();
      if (current() && (hadIdentity || user !== null)) onStatus?.("degraded");
      return;
    }
    void user.getIdTokenResult().then(({ claims }) => {
      if (!current() || ticket !== generation) return;
      if (claims.ownerType !== ownerType || (storeId && claims.storeId !== storeId)) {
        close(); onStatus?.("degraded");
        return;
      }
      hadIdentity = true;
      // Healthy SDK refresh must not reopen a listener, repeat its catch-up,
      // or restart the open listener's watchdog.
      if (stop) return;
      stop = subscribeRevisions({
        sources, isCurrent: current, onChange,
        onStatus: status => {
          if (!current()) return;
          if (status === "live") { attempts = 0; clearTimeout(retry); clearTimeout(watchdog); }
          onStatus?.(status);
        },
        onError: failed,
        listen: (path, next, error) => onSnapshot(doc(db, path),
          { includeMetadataChanges: true },
          snapshot => next({ data: snapshot.data(), fromCache: snapshot.metadata.fromCache, hasPendingWrites: snapshot.metadata.hasPendingWrites }), error),
      });
      watch();
    }).catch(() => { if (current() && ticket === generation) failed(); });
  };
  onStatus?.("connecting");
  const stopAuth = onIdTokenChanged(auth, authenticate);
  const recover = (event: Event) => {
    if (event.type === "a2z:realtime-retry") close();
    if (!stop) { attempts = 0; clearTimeout(retry); authenticate(auth.currentUser); }
  };
  if (typeof window !== "undefined") recoveryEvents.forEach(event => window.addEventListener(event, recover));
  return () => {
    active = false; generation++; stop?.(); stopAuth(); clearTimeout(retry); clearTimeout(watchdog);
    if (typeof window !== "undefined") recoveryEvents.forEach(event => window.removeEventListener(event, recover));
  };
}
